import styled from "@emotion/styled";
import { Box, Stack, Typography } from "@mui/material";
import { GOAL_TYPE_MAP } from "Components/Analyses/Goals/Timeline/utils/goalTypeTranslation";
import { GoalIcon } from "Components/Analyses/_Shared/Goals/GoalIcon";
import type { GoalOverviewType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";
import type { FC } from "react";

type Props = {
  readonly active?: boolean;
  readonly goalType: GoalOverviewType;
  readonly onGoalSelect: (goal: GoalOverviewType) => void;
  readonly disabled?: boolean;
};

const StyledWrapper = styled(Box, {
  shouldForwardProp: isPropValid,
})<{ $active: boolean; $disabled: boolean }>`
  &:hover {
    cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  }

  ${({ theme, $active }) => `
    box-sizing: border-box;
    height: ${theme.spacing(16)};
    border: ${$active ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.colors.borderLight}`};
    border-radius: ${theme.spacing(Radius.M)};
    padding: ${$active ? theme.spacing(2.8) : theme.spacing(Spacing.L)};
    box-shadow: ${theme.shadows[1]};
    background: ${$active ? theme.palette.primary.light : theme.colors.white};

    svg {
      color: ${$active ? theme.palette.text.primary : theme.palette.text.secondary}
    }
  `}
`;

export const GoalTile: FC<Props> = ({ active = false, goalType, onGoalSelect, disabled = false }) => {
  const { t } = useResource();

  return (
    <StyledWrapper $active={active} $disabled={disabled} onClick={() => !disabled && onGoalSelect(goalType)}>
      <Stack spacing={Spacing.M}>
        <GoalIcon type={goalType} />
        <Typography variant="caption" color={active ? undefined : "secondary"}>
          {t(Resources.AnalysisDetail.Goals.GoalType[GOAL_TYPE_MAP[goalType]])}
        </Typography>
      </Stack>
    </StyledWrapper>
  );
};
